// SwipeToDeleteItem.tsx
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import ic_delete from './assets/ic_delete.svg';

type Props = {
  children: React.ReactNode;
  onDelete?: () => void;
  className?: string;
};
const actionWidth = 48;
export default function SwipeToDeleteItem({
  children,
  onDelete,
  className = '',
}: Props) {
  const rowRef = useRef<HTMLDivElement>(null);        // phần content trượt
  const pressedRef = useRef(false);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const openRef = useRef(false);                       // có đang mở sẵn không
  const rafRef = useRef<number | null>(null);

  const setTranslate = (x: number, withTransition = false) => {
    const el = rowRef.current;
    if (!el) return;
    if (withTransition) {
      el.style.transition = 'transform 180ms ease-out';
    } else {
      el.style.transition = 'none';
    }
    // chặn kéo quá giới hạn (-actionWidth đến 0)
    const clamped = Math.min(0, Math.max(-actionWidth, x));
    el.style.transform = `translate3d(${clamped}px,0,0)`;
    currentXRef.current = clamped;
  };

  const animateTo = (target: number) => {
    const el = rowRef.current;
    if (!el) return;
    el.style.transition = 'transform 180ms ease-out';
    el.style.transform = `translate3d(${target}px,0,0)`;
    currentXRef.current = target;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    // chỉ pan ngang, vẫn scroll dọc bình thường
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pressedRef.current = true;
    startXRef.current = e.clientX;
    // nếu đang mở, bắt đầu từ -actionWidth
    if (openRef.current) {
      startXRef.current -= actionWidth;
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pressedRef.current) return;
    const dx = e.clientX - startXRef.current;

    // dùng rAF để tránh layout thrash
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setTranslate(dx));
  };

  const onPointerUp = () => {
    if (!pressedRef.current) return;
    pressedRef.current = false;

    // quyết định mở hay đóng theo ngưỡng
    const opened = currentXRef.current <= -actionWidth * 0.6;
    openRef.current = opened;
    animateTo(opened ? -actionWidth : 0);
  };

  // đóng khi click ra ngoài (nếu đang mở)
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (!openRef.current) return;
      if (!rowRef.current?.parentElement?.contains(e.target as Node)) {
        openRef.current = false;
        animateTo(0);
      }
    };
    document.addEventListener('click', handleDocClick, { capture: true });
    return () => document.removeEventListener('click', handleDocClick, { capture: true } as any);
  }, []);

  return (
    <div className={`std-item ${className}`} style={{ position: 'relative', overflow: 'hidden', alignSelf: 'stretch' }}>
      <ButtonDelete
        className="std-action"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          border: 'none',
          cursor: 'pointer',
        }}
        onClick={onDelete}
        aria-label="Delete"
      >
        <img src={ic_delete} alt="ic_delete" style={{ width: 24, height: 24 }} />
      </ButtonDelete>

      {/* Sliding row */}
      <div
        ref={rowRef}
        className="std-row"
        style={{
          background: '#fff',
          touchAction: 'pan-y',      // cho phép kéo ngang, vẫn scroll dọc
          willChange: 'transform',
          paddingRight: 8,
          userSelect: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => pressedRef.current && onPointerUp()}
      >
        {children}
      </div>
    </div>
  );
}

const ButtonDelete = styled.div`
  display: flex;
  width: 48px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  background: #D2464F;
`;

